import React, { useCallback, useEffect, useState } from 'react';
import { useLang } from '../i18n/LangContext';
import { LANGS } from '../i18n/translations';

const NAV_KEYS = ['about', 'house', 'amenities', 'experiences', 'village', 'arrive', 'contact'];

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

function LangSwitcher({ lang, setLang, scrolled }) {
  const wrapCls = scrolled
    ? 'bg-[#F5F0E6] border-[#EAE4DA]'
    : 'bg-white/15 border-white/25 backdrop-blur-sm';

  return (
    <div className={`hidden md:flex items-center gap-1 rounded-full border p-1 ${wrapCls}`}>
      {LANGS.map((l) => {
        const active = lang === l;
        const activeCls = scrolled ? 'bg-[#2C2A26] text-[#FDFBF7]' : 'bg-white text-[#2C2A26]';
        const idleCls = scrolled ? 'text-[#6b6558] hover:text-[#2C2A26]' : 'text-white/85 hover:text-white';
        return (
          <button
            key={l}
            onClick={() => setLang(l)}
            className={`px-3 py-1 text-[11px] uppercase tracking-widest rounded-full transition-all font-medium ${
              active ? activeCls : idleCls
            }`}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}

function DesktopNav({ scrolled, t }) {
  const linkCls = scrolled ? 'text-[#6b6558] hover:text-[#2C2A26]' : 'text-white/85 hover:text-white';
  return (
    <nav className="hidden lg:flex items-center gap-7">
      {NAV_KEYS.map((k) => (
        <button
          key={k}
          onClick={() => scrollToSection(k)}
          className={`text-[13px] uppercase tracking-[0.14em] font-medium transition-colors ${linkCls}`}
        >
          {t.sections[k]}
        </button>
      ))}
    </nav>
  );
}

function MobileMenuIcon({ open }) {
  if (open) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M6 6l12 12M18 6l-12 12" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4 7h16" strokeLinecap="round" />
      <path d="M4 12h16" strokeLinecap="round" />
      <path d="M4 17h16" strokeLinecap="round" />
    </svg>
  );
}

function MobileMenu({ t, lang, setLang, onClose }) {
  const go = (k) => {
    onClose();
    scrollToSection(k);
  };
  return (
    <div className="lg:hidden bg-[#FDFBF7] border-t border-[#EAE4DA]">
      <div className="px-5 py-4 flex flex-col gap-1">
        {NAV_KEYS.map((k) => (
          <button
            key={k}
            onClick={() => go(k)}
            className="text-left py-3 px-2 text-[#2C2A26] font-serif text-lg border-b border-[#EAE4DA]"
          >
            {t.sections[k]}
          </button>
        ))}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          {LANGS.map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={`px-4 py-2 text-xs uppercase tracking-widest rounded-full border ${
                lang === l
                  ? 'bg-[#2C2A26] text-[#FDFBF7] border-[#2C2A26]'
                  : 'text-[#6b6558] border-[#EAE4DA]'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const onScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  const headerCls = scrolled
    ? 'bg-[rgba(253,251,247,0.92)] backdrop-blur-md border-b border-[#EAE4DA]'
    : 'bg-transparent';

  const brandCls = scrolled ? 'text-[#2C2A26]' : 'text-white drop-shadow';
  const iconBtnCls = scrolled ? 'text-[#2C2A26]' : 'text-white';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerCls}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between py-4 md:py-5">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={`font-serif text-lg md:text-xl tracking-tight transition-colors ${brandCls}`}
        >
          Obach Rural
        </button>

        <DesktopNav scrolled={scrolled} t={t} />

        <div className="flex items-center gap-3">
          <LangSwitcher lang={lang} setLang={setLang} scrolled={scrolled} />
          <button
            className={`lg:hidden p-2 rounded-md ${iconBtnCls}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
          >
            <MobileMenuIcon open={menuOpen} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <MobileMenu t={t} lang={lang} setLang={setLang} onClose={() => setMenuOpen(false)} />
      )}
    </header>
  );
}
